import { zodResolver } from "@hookform/resolvers/zod";
import { useLocalSearchParams, useRouter } from "expo-router";
import Head from "expo-router/head";
import { useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, StyleSheet, TextInput, View } from "react-native";

import { CustomHeader } from "@/components/CustomHeader";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { Button } from "@/components/ui/Button";
import CountryDropdown, {
  type CountryDropdownOption,
} from "@/components/ui/CountryDropdown.web";
import CustomModal from "@/components/ui/CustomModal.web";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { COUNTRIES } from "@/constants/countries";
import { useAuth } from "@/context/AuthContext";
import { resolveError } from "@/validation/messages";
import type { InviteErrorCode, InviteIdentifier } from "@/utils/groupInvite.web";
import { sendGroupInvitation } from "@/utils/groupInvite.web";

import { inviteSchema, type InviteFormValues } from "./invite.schema";

type InviteMode = InviteFormValues["type"];

const errorCodeToI18nKey: Record<InviteErrorCode, string> = {
  USER_NOT_FOUND: "USER_NOT_FOUND",
  USER_ALREADY_INVITED: "USER_ALREADY_INVITED",
  USER_ALREADY_MEMBER: "USER_ALREADY_MEMBER",
  CANNOT_INVITE_YOURSELF: "CANNOT_INVITE_YOURSELF",
  INVITE_WRITE_FAILED: "FAILED_TO_SEND_INVITATION",
};

const phoneCountries = COUNTRIES.filter((c) => "phonePrefix" in c && c.phonePrefix);
const defaultPhonePrefix =
  (phoneCountries[0] as { phonePrefix?: string })?.phonePrefix ?? "";

const defaultsForMode = (mode: InviteMode): InviteFormValues => {
  if (mode === "email") return { type: "email", email: "" };
  if (mode === "username") return { type: "username", username: "" };
  return {
    type: "phone",
    phone: { prefix: defaultPhonePrefix, number: "" },
  };
};

const buildIdentifier = (values: InviteFormValues): InviteIdentifier => {
  if (values.type === "email") return { type: "email", value: values.email };
  if (values.type === "username")
    return { type: "username", value: values.username };
  return {
    type: "phone",
    prefix: values.phone.prefix,
    number: values.phone.number,
  };
};

export default function InviteUserScreen() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const router = useRouter();
  const { groupId, groupName, group } = useLocalSearchParams<{
    groupId: string;
    groupName: string;
    group: string;
  }>();

  const memberIds = useMemo(() => {
    if (!group) return [] as string[];
    try {
      const parsed = JSON.parse(group) as { memberIds?: unknown };
      return Array.isArray(parsed?.memberIds)
        ? (parsed.memberIds as string[])
        : [];
    } catch {
      return [];
    }
  }, [group]);

  const [mode, setMode] = useState<InviteMode>("email");
  const [loading, setLoading] = useState(false);
  const [modalInfo, setModalInfo] = useState<{
    title: string;
    message: string;
  } | null>(null);

  const { control, handleSubmit, reset, formState } = useForm<InviteFormValues>({
    mode: "onTouched",
    resolver: zodResolver(inviteSchema) as any,
    defaultValues: defaultsForMode("email"),
  });

  const switchMode = (next: InviteMode) => {
    if (next === mode) return;
    setMode(next);
    reset(defaultsForMode(next));
  };

  // Watch the whole values object so we can gate the submit button on the
  // mode-relevant field being non-empty. Fields not registered for the current
  // mode (e.g. `username` while on the email tab) return undefined, which
  // reads as "empty" — exactly what we want.
  const watched = useWatch({ control }) as Partial<{
    email: string;
    username: string;
    phone: { prefix?: string; number?: string };
  }>;
  const hasRequiredInput =
    mode === "email"
      ? !!watched?.email?.trim()
      : mode === "username"
        ? !!watched?.username?.trim()
        : !!watched?.phone?.number?.trim();

  const phoneCountry =
    (phoneCountries[0] as {
      phonePlaceholder?: string;
      phoneMaxLength?: number;
      phoneNumberCheck?: (v: string) => string;
    }) ?? undefined;
  // Prefix picker options (currently only entries with a phonePrefix — see
  // constants/countries.ts). The picker exists even for one country to keep
  // the UI honest about what's stored on the invitee's user doc.
  const prefixOptions: CountryDropdownOption[] = phoneCountries.map((c) => ({
    value: (c as { phonePrefix: string }).phonePrefix,
    label: (c as { phonePrefix: string }).phonePrefix,
    iso: c.iso,
  }));

  const onSubmit = handleSubmit(async (values) => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const result = await sendGroupInvitation({
        groupId,
        groupName,
        inviterUid: user.uid,
        memberIds,
        identifier: buildIdentifier(values),
      });
      if (result.kind === "err") {
        setModalInfo({
          title: t("ERROR"),
          message: t(errorCodeToI18nKey[result.code]),
        });
        return;
      }
      setModalInfo({
        title: t("SUCCESS"),
        message: t("INVITATION_SENT_SUCCESSFULLY"),
      });
      setTimeout(() => {
        router.replace({
          pathname: "/(tabs)/groups/group-info",
          params: { group },
        });
      }, 2000);
    } catch (err) {
      console.error("Error sending invitation: ", err);
      setModalInfo({
        title: t("ERROR"),
        message: t("FAILED_TO_SEND_INVITATION"),
      });
    } finally {
      setLoading(false);
    }
  });

  return (
    <>
      <Head>
        <title>{t("PAGE_TITLE_GROUP_INVITE")}</title>
      </Head>
      <CustomHeader
        title={t("INVITE_USERS")}
        type="subtitle"
        variant="tab"
        showBackButton
      />
      <ThemedView style={styles.container}>
        <ThemedText style={styles.label}>
          {t("INVITE_USER_TO", { groupName })}
        </ThemedText>

        <View style={{ marginBottom: 16 }}>
          <SegmentedControl<InviteMode>
            testID="invite-mode"
            value={mode}
            onChange={switchMode}
            options={[
              { value: "email", label: t("INVITE_BY_EMAIL") },
              { value: "username", label: t("INVITE_BY_USERNAME") },
              { value: "phone", label: t("INVITE_BY_PHONE") },
            ]}
          />
        </View>

        {mode === "email" && (
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState }) => (
              <>
                <TextInput
                  style={styles.input}
                  placeholder={t("ENTER_USER_EMAIL")}
                  placeholderTextColor="#9CA3AF"
                  value={(field.value as string) ?? ""}
                  onChangeText={field.onChange}
                  onBlur={field.onBlur}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoComplete="email"
                />
                {fieldState.error && (
                  <ThemedText style={styles.errorText}>
                    {resolveError(fieldState.error, t)}
                  </ThemedText>
                )}
              </>
            )}
          />
        )}

        {mode === "username" && (
          <Controller
            control={control}
            name="username"
            render={({ field, fieldState }) => (
              <>
                <TextInput
                  style={styles.input}
                  placeholder={t("ENTER_USERNAME")}
                  placeholderTextColor="#9CA3AF"
                  value={(field.value as string) ?? ""}
                  onChangeText={field.onChange}
                  onBlur={field.onBlur}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                {fieldState.error && (
                  <ThemedText style={styles.errorText}>
                    {resolveError(fieldState.error, t)}
                  </ThemedText>
                )}
              </>
            )}
          />
        )}

        {mode === "phone" && (
          <View>
            <View style={styles.phoneRow}>
              <Controller
                control={control}
                name="phone.prefix"
                render={({ field }) => (
                  <CountryDropdown
                    value={(field.value as string) ?? defaultPhonePrefix}
                    onChange={field.onChange}
                    placeholder={t("PLEASE_SELECT_COUNTRY")}
                    compact
                    options={prefixOptions}
                  />
                )}
              />
              <Controller
                control={control}
                name="phone.number"
                render={({ field }) => (
                  <TextInput
                    style={styles.phoneInput}
                    placeholder={
                      phoneCountry?.phonePlaceholder ?? t("PHONE_PLACEHOLDER")
                    }
                    placeholderTextColor="#9CA3AF"
                    value={(field.value as string) ?? ""}
                    onChangeText={(text) => {
                      const normalized = phoneCountry?.phoneNumberCheck
                        ? phoneCountry.phoneNumberCheck(text.replace(/\D/g, ""))
                        : text.replace(/\D/g, "");
                      field.onChange(normalized);
                    }}
                    onBlur={field.onBlur}
                    keyboardType="phone-pad"
                    maxLength={phoneCountry?.phoneMaxLength ?? 15}
                    autoComplete="tel"
                  />
                )}
              />
            </View>
            {(() => {
              const phoneErrs = (formState.errors as any).phone as
                | { prefix?: { message?: string }; number?: { message?: string } }
                | undefined;
              const err = phoneErrs?.prefix ?? phoneErrs?.number;
              if (!err?.message) return null;
              return (
                <ThemedText style={styles.errorText}>
                  {resolveError(err as any, t)}
                </ThemedText>
              );
            })()}
          </View>
        )}

        {loading ? (
          <ActivityIndicator style={{ marginTop: 16 }} />
        ) : (
          <Button
            text={t("INVITE_USER")}
            onPress={onSubmit}
            disabled={!hasRequiredInput}
            style={{ width: "100%", maxWidth: "100%", marginTop: 16 }}
          />
        )}
      </ThemedView>
      {modalInfo && (
        <CustomModal
          visible={!!modalInfo}
          onClose={() => setModalInfo(null)}
          title={modalInfo.title}
        >
          <ThemedView>
            <ThemedText>{modalInfo.message}</ThemedText>
            <Button
              text={t("OK")}
              onPress={() => setModalInfo(null)}
              style={{ marginTop: 20 }}
            />
          </ThemedView>
        </CustomModal>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    alignSelf: "center",
    width: "100%",
    maxWidth: 600,
  },
  label: {
    fontSize: 16,
    marginBottom: 8,
  },
  input: {
    width: "100%",
    height: 48,
    borderColor: "#ccc",
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    marginBottom: 8,
    fontSize: 16,
    backgroundColor: "#fafafa",
    color: "#000",
  },
  phoneRow: {
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
    marginBottom: 8,
  },
  phoneInput: {
    flex: 1,
    height: 48,
    borderColor: "#ccc",
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 16,
    backgroundColor: "#fafafa",
    color: "#000",
  },
  errorText: {
    color: "#dc2626",
    fontSize: 13,
    marginBottom: 8,
  },
});
